// ============================================================
// CONFIGURATION MENU GLOBAL UNIFIÉ
// ============================================================
// Ce fichier centralise tous les menus de l'application.
// Chaque élément définit les permissions requises pour y accéder.
// Le Layout filtre automatiquement selon les permissions de l'utilisateur.
// 
// PRINCIPE : Même interface pour tous, seules les actions changent.
// ============================================================

import {
    LayoutDashboard,
    Home,
    Package,
    ShoppingCart,
    ArrowLeftRight,
    TrendingUp,
    FileText,
    Users,
    User,
    ShoppingBasket,
    UtensilsCrossed,
    ChefHat,
    ClipboardList,
    Settings,
    Clock,
    Calendar,
    DollarSign,
    UserCheck,
    CalendarCheck,
    Receipt,
    MessageSquare,
    AlertTriangle,
    Radio,
    Send,
    Mail,
    MapPin,
    Truck,
    Brain,
    Wallet,
    PiggyBank,
    Shield,
    Trophy,
    Building2,
    BookOpen,
    Heart,
    Store,
    CreditCard,
    PieChart,
    UserCog,
    BarChart3,
    Wine,
    GlassWater,
    Globe,
    Tag,
    Warehouse,
    Sparkles,
    Calculator,
    CheckCircle,
    LayoutGrid
} from 'lucide-react';

// ============================================================
// DÉFINITION DES PERMISSIONS PAR RÔLE
// ============================================================
// Ces permissions définissent CE QUE l'utilisateur peut FAIRE,
// pas CE QU'IL VOIT. L'interface est la même pour tous.

export const ROLE_PERMISSIONS = {
    ADMIN: {
        // Accès complet
        canViewDashboard: true,
        canViewAdminDashboard: true,
        canViewStock: true,
        canManageStock: true,
        canViewSales: true,
        canManageSales: true,
        canViewFinance: true,
        canManageFinance: true,
        canViewPersonnel: true,
        canManagePersonnel: true,
        canViewKitchen: true,
        canManageKitchen: true,
        canViewBar: true,
        canManageBar: true,
        canViewSettings: true,
        canManageSettings: true,
        canViewReports: true,
        canViewLiveMonitoring: true,
        canManageEstablishments: true,
        canViewCredentials: true,
        canViewAudit: true,
        canViewLoyalty: true,
        canManageLoyalty: true,
        canViewPerformance: true,
        canViewOrders: true,
        canManageOrders: true,
        // Permissions finance spécifiques
        canViewExpenses: true,
        canViewOvertime: true,
        canViewProfitability: true,
        canViewProfitLoss: true,
        // Budget et Trésorerie
        canManageBudget: true,
        canViewTreasury: true, // Accès Trésorerie/Coffre
        // Reset données test
        canResetData: true,
        // Accès Dashboard Pro
        canViewStockDashboard: true,
        // Liste de courses - Accès complet + validation finale
        canViewShoppingLists: true,
        canCreateShoppingLists: true,
        canValidateShoppingLists: true,
        canFinalValidateShoppingLists: true, // SEUL Admin peut faire validation finale
    },
    DIRECTEUR: {
        canViewDashboard: true,
        canViewAdminDashboard: false,
        canViewStock: true,
        canManageStock: true,
        canViewSales: true,
        canManageSales: true,
        canViewFinance: true,
        canManageFinance: true,
        canViewPersonnel: true,
        canManagePersonnel: false, // Lecture seule pour gestion employés
        canViewKitchen: true,
        canManageKitchen: false, // Lecture seule cuisine
        canViewBar: true,
        canManageBar: false, // Lecture seule bar
        canViewSettings: false,
        canManageSettings: false,
        canViewReports: false, // RETIRÉ - pas d'accès Rapports
        canViewLiveMonitoring: false, // Pas d'accès Supervision Live
        canManageEstablishments: false,
        canViewCredentials: false,
        canViewAudit: false, // Pas d'accès Journal Audit
        canViewLoyalty: true,
        canManageLoyalty: false, // Lecture seule fidélité
        canViewPerformance: false, // Pas d'accès Performance & Bonus
        canViewOrders: true,
        canManageOrders: false, // Lecture seule commandes
        // Permissions finance spécifiques - RETIRÉ Rentabilité, Charges, Rapports
        canViewExpenses: false, // RETIRÉ - pas d'accès Charges
        // Budget - Directeur peut voir mais pas modifier
        canManageBudget: true,
        canViewTreasury: false, // RETIRÉ - pas d'accès Trésorerie/Coffre
        canViewOvertime: true,
        canViewProfitability: false, // RETIRÉ - pas d'accès Rentabilité
        canViewProfitLoss: false, // RETIRÉ - pas d'accès P&L
        // Accès Dashboard Pro
        canViewStockDashboard: true,
        // Liste de courses - Peut valider niveau Directeur (pas final)
        canViewShoppingLists: true,
        canCreateShoppingLists: true,
        canValidateShoppingLists: true, // Validation niveau Directeur
        canFinalValidateShoppingLists: false, // NE PEUT PAS faire validation finale
    },
    DIRECTEUR_ADJOINT: {
        canViewDashboard: true,
        canViewAdminDashboard: false,
        canViewStock: true,
        canManageStock: false, // LECTURE SEULE - pas de modification
        canViewSales: true,
        canManageSales: false, // LECTURE SEULE - pas de modification
        canViewFinance: true,
        canManageFinance: false,
        canViewPersonnel: true,
        canManagePersonnel: false,
        canViewKitchen: true,
        canManageKitchen: false, // LECTURE SEULE cuisine
        canViewBar: true,
        canManageBar: false, // LECTURE SEULE bar
        canViewSettings: false,
        canManageSettings: false,
        canViewReports: true,
        canViewLiveMonitoring: false, // RETIRÉ - Pas d'accès Supervision Live
        canManageEstablishments: false,
        canViewCredentials: false,
        canViewAudit: false,
        canViewLoyalty: true,
        canManageLoyalty: false, // LECTURE SEULE fidélité - pas de scan, pas de validation
        canViewPerformance: false, // RETIRÉ - Pas d'accès Performance & Bonus
        canViewOrders: true,
        canManageOrders: false, // LECTURE SEULE commandes - pas de changement de statut
        // Permissions finance spécifiques - Restreintes
        canViewExpenses: false, // Pas d'accès Charges
        canViewOvertime: false, // Pas d'accès Heures Sup
        canViewProfitability: false, // Pas d'accès Rentabilité
        canViewProfitLoss: false, // Pas d'accès Compte de résultat
        canViewTreasury: false, // Pas d'accès Trésorerie/Coffre
        // Permissions POS spécifiques
        canUsePOS: true, // Peut prendre des commandes
        canSendToKitchen: true, // Peut envoyer en cuisine/bar
        // Accès Dashboard Pro (même que Directeur)
        canViewStockDashboard: true,
        // Liste de courses - Peut créer et soumettre, pas valider
        canViewShoppingLists: true,
        canCreateShoppingLists: true,
        canValidateShoppingLists: false, // NE PEUT PAS valider
    },
    MAITRE_HOTEL: {
        canViewDashboard: true,
        canViewAdminDashboard: false,
        canViewStock: false,
        canManageStock: false,
        canViewSales: true,
        canManageSales: true,
        canViewFinance: false,
        canManageFinance: false,
        canViewPersonnel: true,
        canManagePersonnel: false,
        canViewKitchen: true,
        canViewBar: true,
        canViewSettings: false,
        canManageSettings: false,
        canViewReports: false,
        canViewLiveMonitoring: false,
        canManageEstablishments: false,
        canViewCredentials: false,
        canViewAudit: false,
    },
    SERVEUR: {
        canViewDashboard: true,
        canViewAdminDashboard: false,
        canViewStock: false,
        canManageStock: false,
        canViewSales: true,
        canManageSales: false,
        canViewFinance: false,
        canManageFinance: false,
        canViewPersonnel: false,
        canManagePersonnel: false,
        canViewKitchen: true,    // LECTURE SEULE - peut consulter
        canManageKitchen: false, // NE PEUT PAS valider/modifier
        canViewBar: true,        // LECTURE SEULE - peut consulter
        canManageBar: false,     // NE PEUT PAS valider/modifier
        canViewSettings: false,
        canManageSettings: false,
        canViewReports: false,
        canViewLiveMonitoring: false,
        canManageEstablishments: false,
        canViewCredentials: false,
        canViewAudit: false,
        canViewReservations: true,  // LECTURE SEULE - peut consulter
        canManageReservations: false, // NE PEUT PAS modifier
        canViewMessages: true,    // Accessible - peut envoyer/recevoir messages équipe
        canViewTables: true,      // Accessible
        // Permissions spécifiques serveur
        canUsePOS: true,
        canViewOwnStats: true,
        // Section dédiée serveur lecture seule
        canViewServerDashboard: true, // Accès aux vues lecture seule
    },
    SERVEUSE: {
        canViewDashboard: true,
        canViewAdminDashboard: false,
        canViewStock: false,
        canManageStock: false,
        canViewSales: true,
        canManageSales: false,
        canViewFinance: false,
        canManageFinance: false,
        canViewPersonnel: false,
        canManagePersonnel: false,
        canViewKitchen: false,
        canViewBar: false,
        canViewSettings: false,
        canManageSettings: false,
        canViewReports: false,
        canViewLiveMonitoring: false,
        canManageEstablishments: false,
        canViewCredentials: false,
        canViewAudit: false,
        canUsePOS: true,
        canViewOwnStats: true,
    },
    CAISSIER: {
        canViewDashboard: true,
        canViewAdminDashboard: false,
        canViewStock: false,
        canManageStock: false,
        canViewSales: true,
        canManageSales: true,
        canViewFinance: true,
        canManageFinance: false,
        canViewPersonnel: false,
        canManagePersonnel: false,
        canViewKitchen: false,
        canViewBar: false,
        canViewSettings: false,
        canManageSettings: false,
        canViewReports: false,
        canViewLiveMonitoring: false,
        canManageEstablishments: false,
        canViewCredentials: false,
        canViewAudit: false,
        canUsePOS: true,
        canManageCash: true,
    },
    CHEF_CUISINE: {
        canViewDashboard: true,
        canViewAdminDashboard: false,
        canViewStock: true,
        canManageStock: true,
        canViewSales: false,
        canManageSales: false,
        canViewFinance: false,
        canManageFinance: false,
        canViewPersonnel: false,
        canManagePersonnel: false,
        canViewKitchen: true,
        canViewBar: false,
        canViewSettings: false,
        canManageSettings: false,
        canViewReports: false,
        canViewLiveMonitoring: false,
        canManageEstablishments: false,
        canViewCredentials: false,
        canViewAudit: false,
        canManageRecipes: true,
    },
    CUISINIER: {
        canViewDashboard: false,
        canViewAdminDashboard: false,
        canViewStock: false,
        canManageStock: false,
        canViewSales: false,
        canManageSales: false,
        canViewFinance: false,
        canManageFinance: false,
        canViewPersonnel: false,
        canManagePersonnel: false,
        canViewKitchen: true,
        canViewBar: false,
        canViewSettings: false,
        canManageSettings: false,
        canViewReports: false,
        canViewLiveMonitoring: false,
        canManageEstablishments: false,
        canViewCredentials: false,
        canViewAudit: false,
    },
    BARMAN: {
        canViewDashboard: false,
        canViewAdminDashboard: false,
        canViewStock: false,
        canManageStock: false,
        canViewSales: false,
        canManageSales: false,
        canViewFinance: false,
        canManageFinance: false,
        canViewPersonnel: false,
        canManagePersonnel: false,
        canViewKitchen: false,
        canViewBar: true,
        canViewSettings: false,
        canManageSettings: false,
        canViewReports: false,
        canViewLiveMonitoring: false,
        canManageEstablishments: false,
        canViewCredentials: false,
        canViewAudit: false,
    },
    STOCK_MANAGER: {
        canViewDashboard: true,
        canViewAdminDashboard: false,
        canViewStock: true,
        canManageStock: true,
        canViewSales: false,
        canManageSales: false,
        canViewFinance: false,
        canManageFinance: false,
        canViewPersonnel: false,
        canManagePersonnel: false,
        canViewKitchen: false,
        canViewBar: false,
        canViewSettings: false,
        canManageSettings: false,
        canViewReports: true,
        canViewLiveMonitoring: false,
        canManageEstablishments: false,
        canViewCredentials: false,
        canViewAudit: false,
    },
    EMPLOYE: {
        canViewDashboard: false,
        canViewAdminDashboard: false,
        canViewStock: false,
        canManageStock: false,
        canViewSales: false,
        canManageSales: false,
        canViewFinance: false,
        canManageFinance: false,
        canViewPersonnel: false,
        canManagePersonnel: false,
        canViewKitchen: false,
        canViewBar: false,
        canViewSettings: false,
        canManageSettings: false,
        canViewReports: false,
        canViewLiveMonitoring: false,
        canManageEstablishments: false,
        canViewCredentials: false,
        canViewAudit: false,
        canClock: true,
    },
};

// ============================================================
// MENU GLOBAL UNIFIÉ
// ============================================================
// Tous les menus sont définis ici avec leurs permissions requises.
// Le système filtre automatiquement selon le rôle de l'utilisateur.

export const UNIFIED_MENU_CONFIG = [
    // ==================== ACCUEIL ====================
    {
        id: 'accueil',
        label: 'ACCUEIL',
        icon: Home,
        color: 'text-blue-500',
        bgColor: 'bg-blue-500/10',
        // Aucune permission requise pour voir la section (visible par tous)
        items: [
            { 
                path: '/admin-dashboard', 
                label: 'Dashboard Admin', 
                icon: Shield,
                highlight: true,
                requiredPermission: 'canViewAdminDashboard'
            },
            { 
                path: '/stock-dashboard', 
                label: 'Dashboard Pro', 
                icon: BarChart3,
                highlight: true,
                requiredPermission: 'canViewStockDashboard'
            },
            { 
                path: '/kitchen-dashboard', 
                label: 'Cuisine', 
                icon: ChefHat,
                highlight: true,
                requiredPermission: 'canViewKitchen',
                description: 'Lecture seule pour serveurs'
            },
            { 
                path: '/bar-dashboard', 
                label: 'Bar', 
                icon: Wine,
                highlight: true,
                requiredPermission: 'canViewBar',
                description: 'Lecture seule pour serveurs'
            },
            { 
                path: '/motivation-admin', 
                label: 'Gestion Motivation', 
                icon: Sparkles,
                requiredPermission: 'canManageSettings'  // Admin only
            },
            { 
                path: '/performance', 
                label: 'Performance', 
                icon: TrendingUp,
                requiredPermission: 'canManageSettings'  // Admin only
            },
            { 
                path: '/live-monitoring', 
                label: 'Supervision Live', 
                icon: Radio,
                requiredPermission: 'canViewLiveMonitoring'
            },
            { 
                path: '/client-admin', 
                label: 'Site Client', 
                icon: Globe,
                requiredPermission: 'canManageSettings'
            },
            { 
                path: '/messagerie-pro', 
                label: 'Messagerie Pro', 
                icon: MessageSquare,
                requiredPermission: null // Accessible à tous
            }
        ]
    },
    
    // ==================== VENTE ====================
    {
        id: 'vente',
        label: 'VENTE',
        icon: Store,
        color: 'text-green-500',
        bgColor: 'bg-green-500/10',
        sectionPermission: 'canViewSales',
        items: [
            { 
                path: '/register-management', 
                label: 'Caisses', 
                icon: CreditCard, 
                highlight: true,
                requiredPermission: 'canViewSales'
            },
            { 
                path: '/mes-tables', 
                label: 'Mes Tables', 
                icon: LayoutGrid,
                highlight: true,
                description: 'Vue grille de toutes les tables',
                requiredPermission: 'canViewSales'
            },
            { 
                path: '/pos-v2', 
                label: 'Caisse', 
                icon: Store,
                description: 'Prendre une commande',
                requiredPermission: 'canUsePOS'
            },
            { 
                path: '/menu-products', 
                label: 'Produit/vente', 
                icon: UtensilsCrossed,
                requiredPermission: 'canManageSales'
            },
            { 
                path: '/menu', 
                label: 'Catégories & Tarifs', 
                icon: Tag,
                requiredPermission: 'canManageSales'
            },
            { 
                path: '/formulas', 
                label: 'Formules', 
                icon: BookOpen,
                requiredPermission: 'canManageSales'
            },
            { 
                path: '/reservations', 
                label: 'Réservations', 
                icon: CalendarCheck,
                requiredPermission: 'canViewSales'
            },
            { 
                path: '/daily-sales', 
                label: 'Ventes du jour', 
                icon: TrendingUp,
                requiredPermission: 'canViewSales'
            },
            { 
                path: '/loyalty', 
                label: 'Fidélité', 
                icon: Heart,
                requiredPermission: 'canViewLoyalty'
            }
        ]
    },
    
    // ==================== CUISINE ====================
    {
        id: 'cuisine',
        label: 'CUISINE',
        icon: ChefHat,
        color: 'text-orange-500',
        bgColor: 'bg-orange-500/10',
        sectionPermission: 'canViewKitchen',
        items: [
            { 
                path: '/recipes', 
                label: 'Recettes', 
                icon: BookOpen,
                requiredPermission: 'canViewKitchen'
            },
            { 
                path: '/daily-outputs', 
                label: 'Sorties auto', 
                icon: ClipboardList,
                requiredPermission: 'canViewKitchen'
            }
        ]
    },
    
    // ==================== BAR ====================
    {
        id: 'bar',
        label: 'BAR',
        icon: Wine,
        color: 'text-indigo-500',
        bgColor: 'bg-indigo-500/10',
        sectionPermission: 'canViewBar',
        items: [
            { 
                path: '/category-routing', 
                label: 'Routage catégories', 
                icon: Settings, 
                requiredPermission: 'canManageSettings',
                description: 'Définir Bar ou Cuisine pour chaque catégorie'
            }
        ]
    },
    
    // ==================== STOCK ====================
    {
        id: 'stock',
        label: 'STOCK',
        icon: Package,
        color: 'text-amber-500',
        bgColor: 'bg-amber-500/10',
        sectionPermission: 'canViewStock',
        items: [
            { 
                path: '/products', 
                label: 'Inventaire complet', 
                icon: Warehouse,
                requiredPermission: 'canViewStock'
            },
            { 
                path: '/shopping-lists', 
                label: 'Liste de courses', 
                icon: ShoppingCart,
                highlight: true,
                requiredPermission: 'canViewShoppingLists'
            },
            { 
                path: '/reorder', 
                label: 'Réapprovisionnement', 
                icon: ShoppingCart,
                requiredPermission: 'canManageStock'
            },
            { 
                path: '/purchases', 
                label: 'Achats / Entrées', 
                icon: ShoppingBasket,
                requiredPermission: 'canManageStock'
            },
            { 
                path: '/forecast', 
                label: 'Prévision Achats', 
                icon: TrendingUp,
                requiredPermission: 'canManageStock'
            },
            { 
                path: '/budget', 
                label: 'Gestion Budget', 
                icon: Wallet,
                requiredPermission: 'canManageBudget'
            },
            { 
                path: '/treasury', 
                label: 'Trésorerie (Coffre)', 
                icon: PiggyBank,
                requiredPermission: 'canViewTreasury'
            },
            { 
                path: '/stock-movements', 
                label: 'Mouvements', 
                icon: ArrowLeftRight,
                requiredPermission: 'canViewStock'
            },
            { 
                path: '/suppliers', 
                label: 'Fournisseurs', 
                icon: Truck,
                requiredPermission: 'canManageStock'
            },
            { 
                path: '/alerts', 
                label: 'Alertes stock', 
                icon: AlertTriangle,
                requiredPermission: 'canViewStock'
            }
        ]
    },
    
    // ==================== FINANCE ====================
    {
        id: 'finance',
        label: 'FINANCE',
        icon: BarChart3,
        color: 'text-emerald-500',
        bgColor: 'bg-emerald-500/10',
        sectionPermission: 'canViewFinance',
        items: [
            { 
                path: '/cash-closures', 
                label: 'Clôtures de caisse', 
                icon: Wallet, 
                highlight: true,
                hasBadge: true,
                badgeType: 'pending_closures',
                requiredPermission: 'canViewFinance'
            },
            { 
                path: '/profitability', 
                label: 'Rentabilité', 
                icon: TrendingUp,
                requiredPermission: 'canViewProfitability'
            },
            { 
                path: '/profit-loss', 
                label: 'Compte de résultat', 
                icon: PieChart,
                requiredPermission: 'canViewProfitLoss'
            },
            { 
                path: '/expenses', 
                label: 'Charges', 
                icon: Receipt, 
                requiredPermission: 'canViewExpenses'
            },
            { 
                path: '/disbursements', 
                label: 'Décaissements', 
                icon: DollarSign,
                requiredPermission: 'canManageFinance'
            },
            { 
                path: '/reports', 
                label: 'Rapports', 
                icon: FileText,
                requiredPermission: 'canViewReports'
            },
            { 
                path: '/cash-closing-history', 
                label: 'Historique Clôtures', 
                icon: Calculator,
                requiredPermission: 'canViewFinance'
            },
            { 
                path: '/cash-closing', 
                label: 'Faire une clôture', 
                icon: CheckCircle,
                requiredPermission: 'canManageCash'
            },
            { 
                path: '/server-performance', 
                label: 'Perf. Serveurs', 
                icon: TrendingUp,
                requiredPermission: 'canViewPerformance'  // Même permission que Performance & Bonus
            },
            { 
                path: '/employee-performance', 
                label: 'Performance & Bonus', 
                icon: Trophy,
                requiredPermission: 'canViewPerformance'
            },
            { 
                path: '/daily-reports', 
                label: 'Rapports journaliers', 
                icon: FileText,
                requiredPermission: 'canViewFinance'
            }
        ]
    },
    
    // ==================== PERSONNEL ====================
    {
        id: 'personnel',
        label: 'PERSONNEL',
        icon: Users,
        color: 'text-purple-500',
        bgColor: 'bg-purple-500/10',
        sectionPermission: 'canViewPersonnel',
        items: [
            { 
                path: '/today-attendance', 
                label: 'Présence du jour', 
                icon: CalendarCheck, 
                highlight: true,
                requiredPermission: 'canViewPersonnel'
            },
            { 
                path: '/live-presence', 
                label: 'Présence Live', 
                icon: Users,
                requiredPermission: 'canViewPersonnel'
            },
            { 
                path: '/staff', 
                label: 'Employés', 
                icon: UserCog,
                requiredPermission: 'canManagePersonnel'
            },
            { 
                path: '/planning', 
                label: 'Planning', 
                icon: Calendar,
                requiredPermission: 'canViewPersonnel'
            },
            { 
                path: '/attendance', 
                label: 'Historique', 
                icon: UserCheck,
                requiredPermission: 'canViewPersonnel'
            },
            { 
                path: '/overtime', 
                label: 'Heures Sup', 
                icon: Clock,
                requiredPermission: 'canViewOvertime'
            },
            { 
                path: '/payroll', 
                label: 'Salaires', 
                icon: DollarSign,
                requiredPermission: 'canManageFinance'
            }
        ]
    },
    
    // ==================== MON ESPACE (visible par tous) ====================
    {
        id: 'mon_espace',
        label: 'MON ESPACE',
        icon: User,
        color: 'text-gray-500',
        bgColor: 'bg-gray-500/10',
        // Toujours visible
        items: [
            { 
                path: '/clock', 
                label: 'Pointer', 
                icon: Clock,
                requiredPermission: null // Accessible à tous
            },
            { 
                path: '/profile', 
                label: 'Mon Profil', 
                icon: User,
                requiredPermission: null
            }
        ]
    },
    
    // ==================== ADMINISTRATION ====================
    {
        id: 'administration',
        label: 'ADMINISTRATION',
        icon: Settings,
        color: 'text-red-500',
        bgColor: 'bg-red-500/10',
        sectionPermission: 'canViewSettings',
        items: [
            { 
                path: '/establishments', 
                label: 'Établissements', 
                icon: Building2,
                requiredPermission: 'canManageEstablishments'
            },
            { 
                path: '/admin/credentials', 
                label: 'Identifiants', 
                icon: Shield,
                requiredPermission: 'canViewCredentials'
            },
            { 
                path: '/admin/audit', 
                label: 'Journal audit', 
                icon: FileText,
                requiredPermission: 'canViewAudit'
            },
            { 
                path: '/admin/settings', 
                label: 'Paramètres', 
                icon: Settings,
                requiredPermission: 'canManageSettings'
            },
            { 
                path: '/admin/auto-closure', 
                label: 'Fermeture auto', 
                icon: Clock,
                requiredPermission: 'canManageSettings'
            },
            { 
                path: '/admin-reset', 
                label: 'Remise à zéro', 
                icon: AlertTriangle,
                requiredPermission: 'canResetData'
            },
            { 
                path: '/security', 
                label: 'Contrôle Sécurité', 
                icon: Shield,
                requiredPermission: 'canViewSettings'
            }
        ]
    }
];

// ============================================================
// FONCTION DE FILTRAGE DU MENU
// ============================================================

/**
 * Filtre le menu selon les permissions de l'utilisateur
 * @param {string} role - Rôle de l'utilisateur
 * @returns {Array} Menu filtré
 */
export const getFilteredMenu = (role) => {
    // Si aucun rôle n'est défini, retourner un menu vide
    // pour éviter le "flash" du menu par défaut pendant le chargement
    if (!role) {
        return [];
    }
    
    const permissions = ROLE_PERMISSIONS[role] || ROLE_PERMISSIONS.EMPLOYE;
    
    return UNIFIED_MENU_CONFIG
        .map(section => {
            // Vérifier si la section est visible
            if (section.sectionPermission && !permissions[section.sectionPermission]) {
                // Vérifier si au moins un item est accessible
                const hasAccessibleItem = section.items.some(item => 
                    !item.requiredPermission || permissions[item.requiredPermission]
                );
                if (!hasAccessibleItem) return null;
            }
            
            // Filtrer les items selon les permissions
            const filteredItems = section.items.filter(item => 
                !item.requiredPermission || permissions[item.requiredPermission]
            );
            
            if (filteredItems.length === 0) return null;
            
            return {
                ...section,
                items: filteredItems
            };
        })
        .filter(Boolean);
};

/**
 * Vérifie si l'utilisateur a une permission spécifique
 * @param {string} role - Rôle de l'utilisateur
 * @param {string} permission - Permission à vérifier
 * @returns {boolean}
 */
export const hasPermission = (role, permission) => {
    const permissions = ROLE_PERMISSIONS[role] || {};
    return permissions[permission] === true;
};

/**
 * Retourne toutes les permissions d'un rôle
 * @param {string} role - Rôle de l'utilisateur
 * @returns {Object}
 */
export const getRolePermissions = (role) => {
    return ROLE_PERMISSIONS[role] || ROLE_PERMISSIONS.EMPLOYE;
};

export default UNIFIED_MENU_CONFIG;
